import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import { LuUser, LuFileCheck } from "react-icons/lu";
import { BsBoxArrowRight, BsCardChecklist } from "react-icons/bs";
import Tooltip from "@mui/material/Tooltip";
import { format, isValid, differenceInMinutes, parseISO } from "date-fns";
import api from "../../api";
import Logo from "../../components/Logo";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

export default function Dashboard() {
  const [students, setStudents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [presentCount, setPresentCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const getApiData = async () => {
      const { data } = await api.get(`${process.env.REACT_APP_API_BASE_URL}students/college`);
      setStudents(data);
    };
    getApiData();
  }, []);

  const handleActionClick = async (type, idOrEmail) => {
    setModalTitle(type);
    try {
      let url = "";
      if (type === "profile") url = `${process.env.REACT_APP_API_BASE_URL}students/profile/${idOrEmail}`;
      if (type === "attendance") url = `${process.env.REACT_APP_API_BASE_URL}attendance/student/${idOrEmail}`;
      if (type === "report") url = `${process.env.REACT_APP_API_BASE_URL}report/student/${idOrEmail}`;
      const { data } = await api.get(url);
      setModalContent(data || []);
      if (type === "attendance") {
        const present = data.filter((a) => a.status === "Present").length;
        setPresentCount(present);
      }
      const student = students.find((s) =>
        type === "report" ? s._id === idOrEmail : s.email === idOrEmail
      );
      setSelectedStudentName(student?.name || "Student");
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      setModalContent([]);
      setModalOpen(true);
    }
  };

  const formatAttendanceDataForExport = (data) => {
    return data.map((attendance, index) => {
      const clockIn = new Date(attendance.clockInTime);
      const clockOut = new Date(attendance.clockOutTime);
      const totalMinutes = isValid(clockIn) && isValid(clockOut)
        ? differenceInMinutes(clockOut, clockIn)
        : 0;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return {
        "#": index + 1,
        Date: isValid(clockIn) ? format(clockIn, "dd MMM yyyy") : "Invalid",
        "Clock In": isValid(clockIn) ? format(clockIn, "hh:mm a") : "Invalid",
        "Clock Out": isValid(clockOut) ? format(clockOut, "hh:mm a") : "Invalid",
        Total: `${hours}h ${minutes}m`,
      };
    });
  };

  const downloadReportAsPDF = () => {
    const doc = new jsPDF();
    doc.text(`Reports - ${selectedStudentName}`, 14, 10);
    const tableData = modalContent.map((report, index) => {
      const createdAt = new Date(report.createdAt);
      return [
        index + 1,
        report.date,
        report.topic,
        report.description,
        report.status,
        isValid(createdAt) ? format(createdAt, "dd MMM yyyy") : "Invalid",
      ];
    });
    autoTable(doc, {
      head: [["#", "Date", "Topic", "Description", "Status", "Created"]],
      body: tableData,
    });
    doc.save(`${selectedStudentName.replace(/\s+/g, "_")}_report.pdf`);
  };

  const downloadReportAsExcel = () => {
    const worksheetData = modalContent.map((report, index) => {
      const createdAt = new Date(report.createdAt);
      return {
        "#": index + 1,
        Date: report.date,
        Topic: report.topic,
        Description: report.description,
        Status: report.status,
        Created: isValid(createdAt) ? format(createdAt, "dd MMM yyyy") : "Invalid",
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(fileData, `${selectedStudentName.replace(/\s+/g, "_")}_report.xlsx`);
  };

  const downloadAttendanceAsPDF = () => {
    const doc = new jsPDF();
    doc.text(`Attendance - ${selectedStudentName}`, 14, 10);
    const tableData = formatAttendanceDataForExport(modalContent).map((row) => Object.values(row));
    autoTable(doc, {
      head: [["#", "Date", "Clock In", "Clock Out", "Total"]],
      body: tableData,
    });
    doc.save(`${selectedStudentName.replace(/\s+/g, "_")}_attendance.pdf`);
  };

  const downloadAttendanceAsExcel = () => {
    const formatted = formatAttendanceDataForExport(modalContent);
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(fileData, `${selectedStudentName.replace(/\s+/g, "_")}_attendance.xlsx`);
  };

  const renderCalendarTile = ({ date }) => {
    const match = modalContent.find((entry) =>
      format(new Date(entry.date), "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
    );
    if (match?.status === "Present") return <span className="text-green-600">✅</span>;
    if (match?.status === "Absent") return <span className="text-red-600">❌</span>;
    return <span>—</span>;
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 2, minWidth: 150, headerClassName: "custom-header" },
    { field: "phone", headerName: "Phone", flex: 2, minWidth: 120, headerClassName: "custom-header" },
    { field: "email", headerName: "Email", flex: 3, minWidth: 180, headerClassName: "custom-header" },
    {
      field: "actions", headerName: "Actions", flex: 2, minWidth: 160, headerClassName: "custom-header",
      renderCell: (params) => (
        <div className="flex gap-3 items-center">
          <Tooltip title="Profile" arrow>
            <LuUser className="h-5 w-5 cursor-pointer hover:text-orange-500" onClick={() => handleActionClick("profile", params.row.email)} />
          </Tooltip>
          <Tooltip title="Attendance" arrow>
            <BsCardChecklist className="h-5 w-5 cursor-pointer hover:text-orange-500" onClick={() => handleActionClick("attendance", params.row.email)} />
          </Tooltip>
          <Tooltip title="Report" arrow>
            <LuFileCheck className="h-5 w-5 cursor-pointer hover:text-orange-500" onClick={() => handleActionClick("report", params.row._id)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/college/login");
  };

  return (
    <div className="w-full min-h-screen bg-white">
      <div className="flex flex-col md:flex-row md:items-center justify-between px-4 py-3 bg-gray-100 border-b shadow-sm gap-2">
        <div className="w-full md:w-[200px]">
          <Logo height={10} />
        </div>
        <div className="text-sm text-gray-700 md:text-base">👋 Welcome to Dashboard</div>
        <h2 className="text-sm md:text-lg text-gray-700 font-semibold">
          <span className="text-blue-600">{students[0]?.college?.name}</span>
          <button onClick={handleLogout} className="flex items-center w-full px-4 py-2 text-red-600 hover:bg-gray-100">
            <BsBoxArrowRight className="w-4 h-4 mr-2" /> Logout
          </button>
        </h2>
      </div>

      <div className="p-4 overflow-x-auto">
        <DataGrid
          rows={students || []}
          columns={columns}
          getRowId={(row) => row._id}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10]}
          autoHeight
          sx={{
            // Glassy root container
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            border: "none",
            borderRadius: 3,
            backdropFilter: "blur(8px)",
          
            // Top header row
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(12px)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            },
          
            // Footer container (bottom row)
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "rgba(255,255,255,0.05)",
              borderTop: "1px solid rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
            },
          
            // Ensure rows have no override from CSS variable
            "& .MuiDataGrid-row": {
              backgroundColor: "transparent !important",
            },
          
            // Optional: override containerBackground if needed
            "--DataGrid-containerBackground": "transparent",
          }}
        />
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} className="flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b">
            <h2 className="text-lg font-semibold capitalize">{modalTitle}</h2>
            <button className="text-lg hover:text-red-500" onClick={() => setModalOpen(false)}>&times;</button>
          </div>
          <div className="px-4 pt-2 pb-4">
            {modalTitle === "attendance" && Array.isArray(modalContent) && modalContent.length > 0 && (
              <>
                <div className="mb-4">
                  <p className="text-sm text-gray-700 mb-1 font-medium">✅ Total Presents: {presentCount}</p>
                  <Calendar
                    tileContent={renderCalendarTile}
                    tileClassName="text-center"
                    className="w-full"
                  />
                </div>
                <div className="flex gap-3 mb-4">
                  <button onClick={downloadAttendanceAsPDF} className="px-4 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600">Download PDF</button>
                  <button onClick={downloadAttendanceAsExcel} className="px-4 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600">Download Excel</button>
                </div>
              </>
            )}
                        {modalTitle === "profile" && modalContent && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Profile</h3>
                <div className="space-y-2">
                  <p><strong>Name:</strong> {modalContent.name}</p>
                  <p><strong>Email:</strong> {modalContent.email}</p>
                  <p><strong>Phone:</strong> {modalContent.phone}</p>
                  <p><strong>College:</strong> {modalContent.college?.name}</p>
                  <p><strong>Year:</strong> {modalContent.year}</p>
                  <p><strong>Branch:</strong> {modalContent.branch}</p>
                  <p><strong>Course:</strong> {modalContent.course}</p>
                  <p><strong>Approved:</strong> {modalContent.approved ? "Yes" : "No"}</p>
                </div>
              </div>
            )}

{modalTitle === "report" && Array.isArray(modalContent) && modalContent.length > 0 && (
  <>
    <div className="flex gap-3 mb-4">
      <button onClick={downloadReportAsPDF} className="px-4 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600">Download PDF</button>
      <button onClick={downloadReportAsExcel} className="px-4 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600">Download Excel</button>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm border">
        <thead className="bg-gray-100 text-gray-600">
          <tr>
            <th className="px-2 py-2 border">Sr.No</th>
            <th className="px-2 py-2 border">Date</th>
            <th className="px-2 py-2 border">Topic</th>
            <th className="px-2 py-2 border">Description</th>
          </tr>
        </thead>
        <tbody>
          {modalContent.map((report, idx) => (
            <tr key={idx} className="hover:bg-gray-50">
              <td className="px-2 py-2 border" align="center">{idx + 1}</td>
              <td className="px-2 py-2 border" align="center">{report.date}</td>
              <td className="px-2 py-2 border" align="center">{report.topic}</td>
              <td className="px-2 py-2 border" align="center">{report.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
)}


          </div>
        </div>
      </Modal>
    </div>
  );
}


